import { Keyboard } from '@capacitor/keyboard';
import memoize from 'lodash.memoize';
import { Observable } from 'rxjs';
import { asap } from 'rxjs/scheduler/asap';
import { WlWindow } from 'typings/globals/cordova';
import getScrollParent from 'utils/getScrollParent';
import { isCordova } from 'utils/platform';

declare const window: WlWindow;

const orientationchange$ = Observable.fromEvent(
  window,
  'orientationchange',
).share() as unknown as Observable<Event>;

export const onResize$ = memoize(
  (element: EventTarget = window): Observable<unknown> =>
    Observable.merge(
      Observable.of('init'),
      Observable.fromEvent(document, 'ready'),
      Observable.fromEvent(document, 'deviceready'),
      Observable.fromEvent(element, 'resize'),
      Observable.fromEvent(window, 'resize'),
      orientationchange$,
      orientationchange$.delay(200), // iOS needs multiple attempts to resize
      orientationchange$.delay(500),
      asap,
    )
      .auditTime(200)
      .do((e) => console.debug('onResize', e))
      .shareReplay(1),
);

export function scrollToFocusedOnKeyboard() {
  if (!isCordova()) {
    return () => {};
  }
  const PADDING = 30;
  Keyboard.addListener('keyboardDidShow', (info) => {
    const element = document.activeElement as HTMLElement | null;
    if (!element) {
      return;
    }
    const offsetAttr = element.getAttribute?.('data-scrolloffset');
    const offset = parseInt(offsetAttr ?? '0', 10) || 0;

    const elementRect = element.getBoundingClientRect();
    if (elementRect.bottom >= window.innerHeight - offset) {
      const parent = getScrollParent(element);
      if (parent && typeof (parent as any).scrollBy === 'function') {
        (parent as any).scrollBy({
          behavior: 'smooth',
          top: elementRect.bottom - window.innerHeight + offset + PADDING,
        });
      } else if (parent && 'scrollTop' in (parent as any)) {
        (parent as any).scrollTop +=
          elementRect.bottom - window.innerHeight + offset + PADDING;
      }
    }
  });

  return () => {
    Keyboard.removeAllListeners();
  };
}
